import PropTypes from "prop-types";
import React from "react";
import classNames from "classnames";
import { forceCheck } from "react-lazyload";
import camelCase from "lodash/camelCase";
import includes from "lodash/includes";
import { FormattedDate, FormattedMessage } from "react-intl";
import { PRODUCTS_FILTERS_KEY } from "app/constants";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import {
	sendTagOnProductsFilter,
	sendTagOnProductsFilterOpen,
	sendTagOnProductsFilterRemove,
} from "app/utils/analytics";
import "@brand/ProductsFilter/products-filter.scss";
import Separator from "app/pages/.shared/Separator";
import onClickOutside from "react-onclickoutside";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import IconArrowUp from "app/pages/.shared/static/icons/IconArrowUp";

class ProductsFilter extends React.PureComponent {
	constructor() {
		super();
		this.state = {
			selectedTab: -1, // par defaut aucun panneau filtre ne doit être ouvert
		};
		this.handleSelect = this.handleSelect.bind(this);
		this.productFiltersRef = React.createRef();
	}

	componentDidMount() {
		this.productFiltersRef.current.classList.add("products-filters--ready");
	}

	handleSelect(index) {
		this.setState(
			{
				// eslint-disable-next-line react/no-access-state-in-setstate
				selectedTab: this.state.selectedTab === index ? -1 : index, // si on reclic sur un onglet, on ferme le panneau
			},
			() => {
				forceCheck(); // forcer l'affichage des photos des produits filtrés
				this.props.scrollToListingHeader();
			}
		);
	}

	handleClickOutside() {
		this.setState({ selectedTab: -1 });
	}

	render() {
		const {
			productsFilters,
			visibleFilters,
			selectedFacets,
			productsActiveFilters,
			setProductsFilter,
			unsetProductsFilter,
			destinationFilterValues = [],
			topicFilterValues = [],
			periodFilterValues = [],
			monthsFilterValues = [],
		} = this.props;

		const isDestinationFilterActive = productsFilters.destination;

		const isMonthFilterActive = productsFilters.month;

		const isTopicFilterActive = productsFilters.topic;

		const isBadgeFilterActive = productsFilters.badge;

		const isPeriodFilterActive = productsFilters.period;

		const isDiscountPercentageFacetActive = productsFilters.discountPercentage;

		const percentageCount = selectedFacets.discountPercentage.true
			? selectedFacets.discountPercentage.true.length
			: 0;

		const percentageClassname = classNames({
			"products-filters__facet": true,
			"products-filters__facet--active": isDiscountPercentageFacetActive,
			"products-filters__facet--available": percentageCount > 0,
		});

		const isNewFacetActive = productsFilters.startAt;

		const newCount = selectedFacets.startAt.true ? selectedFacets.startAt.true.length : 0;

		const newClassname = classNames({
			"products-filters__facet": true,
			"products-filters__facet--active": isNewFacetActive,
			"products-filters__facet--available": newCount > 0,
		});

		const is48hFacetActive = productsFilters.endAt;

		const almostExpiredCount = selectedFacets.endAt.true ? selectedFacets.endAt.true.length : 0;

		const almostExpiredClassname = classNames({
			"products-filters__facet": true,
			"products-filters__facet--active": is48hFacetActive,
			"products-filters__facet--available": almostExpiredCount > 0,
		});

		let topicTabHeaderLabel = (
			<FormattedMessage id="listing.filter.theme.label" tagName="span" />
		);

		if (isNewFacetActive) {
			topicTabHeaderLabel = (
				<div className="products-filters__filter-name">
					<FormattedMessage id="listing.filter.new" />
				</div>
			);
		} else if (is48hFacetActive) {
			topicTabHeaderLabel = (
				<div className="products-filters__filter-name">
					<FormattedMessage id="listing.filter.48h" />
				</div>
			);
		} else if (productsFilters.topic) {
			topicTabHeaderLabel = (
				<div className="products-filters__filter-name">{productsFilters.topic}</div>
			);
		} else if (productsFilters.badge) {
			topicTabHeaderLabel = (
				<div className="products-filters__filter-name">{productsFilters.badge}</div>
			);
		} else if (isDiscountPercentageFacetActive) {
			topicTabHeaderLabel = (
				<div className="products-filters__filter-name">
					<FormattedMessage
						id="listing.filter.percentage"
						values={{ percentage: "50" }}
					/>
				</div>
			);
		}

		let periodTabHeaderLabel = (
			<FormattedMessage id="listing.filter.calendrier.label" tagName="span" />
		);

		if (isMonthFilterActive) {
			periodTabHeaderLabel = (
				<div className="products-filters__filter-name">
					<FormattedDate
						value={Number(productsFilters.month)}
						month="long"
						year="numeric"
					>
						{monthLabel => <div>{monthLabel}</div>}
					</FormattedDate>
				</div>
			);
		} else if (isPeriodFilterActive) {
			periodTabHeaderLabel = (
				<div className="products-filters__filter-name">{productsFilters.period}</div>
			);
		}

		let destinationTabIcon = <IconArrowDown height={15} width={15} />;
		if (isDestinationFilterActive) {
			destinationTabIcon = (
				<i
					className="icon icon--circle-cross-grey"
					onClick={e => {
						e.stopPropagation();
						unsetProductsFilter(PRODUCTS_FILTERS_KEY.DESTINATION);
						sendTagOnProductsFilterRemove("destination");
					}}
				/>
			);
		} else if (this.state.selectedTab === 0) {
			destinationTabIcon = <IconArrowUp height={15} width={15} />;
		}

		let topicTabIcon = <IconArrowDown height={15} width={15} />;
		if (
			isTopicFilterActive ||
			isBadgeFilterActive ||
			isNewFacetActive ||
			is48hFacetActive ||
			isDiscountPercentageFacetActive
		) {
			topicTabIcon = (
				<i
					className="icon icon--circle-cross-grey"
					onClick={e => {
						e.stopPropagation();
						unsetProductsFilter(PRODUCTS_FILTERS_KEY.TOPIC);
						unsetProductsFilter(PRODUCTS_FILTERS_KEY.BADGE);
						unsetProductsFilter(PRODUCTS_FILTERS_KEY.START_AT);
						unsetProductsFilter(PRODUCTS_FILTERS_KEY.DISCOUNT_PERCENTAGE);
						unsetProductsFilter(PRODUCTS_FILTERS_KEY.END_AT);
						sendTagOnProductsFilterRemove("theme");
					}}
				/>
			);
		} else if (this.state.selectedTab === 1) {
			topicTabIcon = <IconArrowUp height={15} width={15} />;
		}

		let periodTabIcon = <IconArrowDown height={15} width={15} />;
		if (isPeriodFilterActive || isMonthFilterActive) {
			periodTabIcon = (
				<i
					className="icon icon--circle-cross-grey"
					onClick={e => {
						e.stopPropagation();
						unsetProductsFilter(PRODUCTS_FILTERS_KEY.PERIOD);
						unsetProductsFilter(PRODUCTS_FILTERS_KEY.MONTH);
						sendTagOnProductsFilterRemove("calendar");
					}}
				/>
			);
		} else if (this.state.selectedTab === 2) {
			periodTabIcon = <IconArrowUp height={15} width={15} />;
		}

		const destinationTabClass = classNames({
			"products-filters__header": true,
			"products-filters__header--active": isDestinationFilterActive,
		});

		const topicTabClass = classNames({
			"products-filters__header": true,
			"products-filters__header--active":
				isTopicFilterActive ||
				isBadgeFilterActive ||
				isNewFacetActive ||
				is48hFacetActive ||
				isDiscountPercentageFacetActive,
		});

		const periodTabClass = classNames({
			"products-filters__header": true,
			"products-filters__header--active": isPeriodFilterActive || isMonthFilterActive,
		});

		return (
			<div
				className="products-filters"
				ref={this.productFiltersRef}
				data-cy="products-filters"
			>
				<Tabs selectedIndex={this.state.selectedTab} onSelect={this.handleSelect}>
					<TabList className={["products-filters__headers", "react-tabs__tab-list"]}>
						<Tab
							className={[destinationTabClass, "react-tabs__tab"]}
							onClick={() => {
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.DESTINATION);
								sendTagOnProductsFilterOpen("destination");
							}}
						>
							{isDestinationFilterActive ? (
								<div className="products-filters__filter-name">
									{productsFilters.destination}
								</div>
							) : (
								<FormattedMessage
									id="listing.filter.destination.label"
									tagName="span"
								/>
							)}

							{destinationTabIcon}
						</Tab>
						<Tab
							className={[topicTabClass, "react-tabs__tab"]}
							onClick={() => {
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.TOPIC);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.BADGE);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.START_AT);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.DISCOUNT_PERCENTAGE);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.END_AT);
								sendTagOnProductsFilterOpen("theme");
							}}
						>
							{topicTabHeaderLabel}

							{topicTabIcon}
						</Tab>
						<Tab
							className={[periodTabClass, "react-tabs__tab"]}
							onClick={() => {
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.PERIOD);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.MONTH);
								sendTagOnProductsFilterOpen("calendar");
							}}
						>
							{periodTabHeaderLabel}

							{periodTabIcon}
						</Tab>
					</TabList>

					<TabPanel
						className={[
							"products-filters__content",
							"products-filters__content--destination",
							"react-tabs__tab-panel",
						]}
					>
						<ul className="products-filters__facets" data-cy="products-filters-facets">
							{destinationFilterValues.map(destination => {
								const destinationKey = camelCase(destination);
								const available = includes(visibleFilters.destination, destination);
								const active = includes(productsActiveFilters, destination);
								const count = selectedFacets.destination[destination]
									? selectedFacets.destination[destination].length
									: 0;
								const facetClassName = classNames({
									"products-filters__facet": true,
									"products-filters__facet--active": active,
									"products-filters__facet--available": available,
								});

								return (
									<li
										className={facetClassName}
										key={destinationKey}
										onClick={() => {
											setProductsFilter({
												[PRODUCTS_FILTERS_KEY.DESTINATION]: destination,
											});
											this.handleSelect(-1); // fermeture de l'onglet
											sendTagOnProductsFilter("destination", destination);
										}}
									>
										<div>{destination}</div>
										<div>({count})</div>
									</li>
								);
							})}
						</ul>
					</TabPanel>

					<TabPanel
						className={[
							"products-filters__content",
							"products-filters__content--topic",
							"react-tabs__tab-panel",
						]}
					>
						<div className="products-filters__facets">
							{topicFilterValues.map(topic => {
								const topicKey = camelCase(topic);
								const available = includes(visibleFilters.topic, topic);
								const active = includes(productsActiveFilters, topic);
								const count = selectedFacets.topic[topic]
									? selectedFacets.topic[topic].length
									: 0;
								const facetClassName = classNames({
									"products-filters__facet": true,
									"products-filters__facet--active": active,
									"products-filters__facet--available": available,
								});

								return (
									<div
										className={facetClassName}
										key={topicKey}
										onClick={() => {
											unsetProductsFilter(PRODUCTS_FILTERS_KEY.START_AT);
											unsetProductsFilter(PRODUCTS_FILTERS_KEY.END_AT);
											setProductsFilter({
												[PRODUCTS_FILTERS_KEY.TOPIC]: topic,
											});
											this.handleSelect(-1); // fermeture de l'onglet
											sendTagOnProductsFilter("theme", topic);
										}}
									>
										<div>{topic}</div>
										<div>({count})</div>
									</div>
								);
							})}

							<Separator design="middle" />

							<div
								className={newClassname}
								onClick={() => {
									unsetProductsFilter(PRODUCTS_FILTERS_KEY.END_AT);
									unsetProductsFilter(PRODUCTS_FILTERS_KEY.BADGE);
									unsetProductsFilter(PRODUCTS_FILTERS_KEY.TOPIC);
									setProductsFilter({ [PRODUCTS_FILTERS_KEY.START_AT]: true });
									this.handleSelect(-1); // fermeture de l'onglet
									sendTagOnProductsFilter("theme", "nouveautes");
								}}
							>
								<FormattedMessage id="listing.filter.new" /> ({newCount})
							</div>

							<div
								className={almostExpiredClassname}
								onClick={() => {
									unsetProductsFilter(PRODUCTS_FILTERS_KEY.START_AT);
									unsetProductsFilter(PRODUCTS_FILTERS_KEY.BADGE);
									unsetProductsFilter(PRODUCTS_FILTERS_KEY.TOPIC);
									setProductsFilter({ [PRODUCTS_FILTERS_KEY.END_AT]: true });
									this.handleSelect(-1); // fermeture de l'onglet
									sendTagOnProductsFilter("theme", "48h");
								}}
							>
								<FormattedMessage id="listing.filter.48h" /> ({almostExpiredCount})
							</div>

							<div
								className={percentageClassname}
								onClick={() => {
									unsetProductsFilter(PRODUCTS_FILTERS_KEY.START_AT);
									unsetProductsFilter(PRODUCTS_FILTERS_KEY.BADGE);
									unsetProductsFilter(PRODUCTS_FILTERS_KEY.TOPIC);
									setProductsFilter({
										[PRODUCTS_FILTERS_KEY.DISCOUNT_PERCENTAGE]: true,
									});
									this.handleSelect(-1); // fermeture de l'onglet
									sendTagOnProductsFilter("theme", "50%");
								}}
							>
								<FormattedMessage
									id="listing.filter.percentage"
									values={{ percentage: "50" }}
								/>{" "}
								({percentageCount})
							</div>
						</div>
					</TabPanel>

					<TabPanel
						className={[
							"products-filters__content",
							"products-filters__content--period",
							"react-tabs__tab-panel",
						]}
					>
						{visibleFilters.month.length === 0 && visibleFilters.period === 0 ? (
							<FormattedMessage id="listing.filter.no.period.facet.message" />
						) : (
							<div className="products-filters__facets">
								<div className="products-filters__months">
									<div className="products-filters__title">
										<FormattedMessage id="listing.filter.month.label" />
									</div>
									{monthsFilterValues.map(month => {
										const monthKey = camelCase(month);
										const available = includes(visibleFilters.month, month);
										const active = includes(productsActiveFilters, month);
										const count = selectedFacets.month[month]
											? selectedFacets.month[month].length
											: 0;
										const facetClassName = classNames({
											"products-filters__facet": true,
											"products-filters__facet--active": active,
											"products-filters__facet--available": available,
										});

										return (
											<div
												className={facetClassName}
												key={monthKey}
												onClick={() => {
													setProductsFilter({
														[PRODUCTS_FILTERS_KEY.MONTH]: month,
													});
													this.handleSelect(-1); // fermeture de l'onglet
													sendTagOnProductsFilter("month", month);
												}}
											>
												<FormattedDate
													value={Number(month)}
													month="long"
													year="numeric"
												/>
												<div>({count})</div>
											</div>
										);
									})}
								</div>

								<div className="products-filters__periods">
									<div className="products-filters__title">
										<FormattedMessage id="listing.filter.period.label" />
									</div>
									{periodFilterValues.map(period => {
										const periodKey = camelCase(period);
										const available = includes(visibleFilters.period, period);
										const active = includes(productsActiveFilters, period);
										const count = selectedFacets.period[period]
											? selectedFacets.period[period].length
											: 0;
										const facetClassName = classNames({
											"products-filters__facet": true,
											"products-filters__facet--active": active,
											"products-filters__facet--available": available,
										});

										return (
											<div
												className={facetClassName}
												key={periodKey}
												onClick={() => {
													setProductsFilter({
														[PRODUCTS_FILTERS_KEY.PERIOD]: period,
													});
													this.handleSelect(-1); // fermeture de l'onglet
													sendTagOnProductsFilter("period", period);
												}}
											>
												<div>{period}</div>
												<div>({count})</div>
											</div>
										);
									})}
								</div>
							</div>
						)}
					</TabPanel>
				</Tabs>
			</div>
		);
	}
}

ProductsFilter.propTypes = {
	setProductsFilter: PropTypes.func,
	scrollToListingHeader: PropTypes.func,
	unsetProductsFilter: PropTypes.func,
	visibleFilters: PropTypes.object,
	selectedFacets: PropTypes.object,
	productsActiveFilters: PropTypes.arrayOf(PropTypes.string),
	destinationFilterValues: PropTypes.arrayOf(PropTypes.string),
	periodFilterValues: PropTypes.arrayOf(PropTypes.string),
	productsFilters: PropTypes.object,
	topicFilterValues: PropTypes.arrayOf(PropTypes.string),
	monthsFilterValues: PropTypes.arrayOf(PropTypes.string),
};

export default onClickOutside(ProductsFilter);
